import React, { useRef } from 'react';
import { LOGO_URL } from '../constants/branding';
import { FaTimes, FaDownload, FaPrint, FaCreditCard, FaBuilding, FaPhone, FaEnvelope, FaGlobe, FaUser, FaCalendarAlt, FaMapMarkerAlt, FaQuestionCircle, FaComments, FaCheckCircle } from 'react-icons/fa';
import html2pdf from 'html2pdf.js';

const ProfessionalInvoiceTemplate = ({ invoice, customerData, onClose, onDownload, onPrint, isDownloading }) => {
  const invoiceRef = useRef(null);

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-AU', {
      style: 'currency',
      currency: 'AUD'
    }).format(amount);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-AU', {
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const getStatusColor = (status) => {
    switch (status) {
      case 'paid': return 'bg-green-100 text-green-800';
      case 'pending': return 'bg-yellow-100 text-yellow-800';
      case 'failed': return 'bg-red-100 text-red-800';
      default: return 'bg-gray-100 text-gray-800';
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'paid': return '✓';
      case 'pending': return '⏳';
      case 'failed': return '✗';
      default: return '?';
    }
  };

  const getBillingTypeLabel = (billingType) => {
    switch (billingType) {
      case 'pro-rated': return 'Pro-rated';
      case 'full-month': return 'Full Month';
      default: return billingType;
    }
  };

  // PDF Generation Function
  const generatePDF = async () => {
    // If a custom onDownload function is provided (e.g., server-side generation), use it
    if (onDownload) {
      onDownload();
      return;
    }

    if (!invoiceRef.current) return;

    // Get button and store original content
    const downloadBtn = document.querySelector('[data-pdf-download]');
    let originalButtonContent = '';
    if (downloadBtn) {
      originalButtonContent = downloadBtn.innerHTML;
    }

    try {
      // Add class to hide buttons during PDF generation
      const modalContainer = document.querySelector('.fixed.inset-0');
      if (modalContainer) {
        modalContainer.classList.add('pdf-generating');
      }

      // Show loading state
      if (downloadBtn) {
        downloadBtn.disabled = true;
        downloadBtn.innerHTML = '<span>Generating PDF...</span>';
      }

      // Configure PDF options with better page break handling
      const opt = {
        margin: [5, 5, 5, 5],
        filename: `Peaknet_Invoice_${invoice?.invoiceNumber || 'INV'}.pdf`,
        image: { type: 'jpeg', quality: 0.95 },
        html2canvas: {
          scale: 2,
          useCORS: true,
          allowTaint: true,
          backgroundColor: '#ffffff',
          logging: false,
          letterRendering: true,
          windowWidth: invoiceRef.current.offsetWidth || 800,
          windowHeight: invoiceRef.current.offsetHeight || window.innerHeight,
          scrollY: 0,
          scrollX: 0
        },
        jsPDF: {
          unit: 'mm',
          format: 'a4',
          orientation: 'portrait',
          compress: true
        },
        pagebreak: {
          mode: 'avoid-all',
          before: '.page-break-before',
          after: '.page-break-after',
          avoid: ['.no-break', '.invoice-section-container', 'tr.no-break', 'thead', 'tfoot']
        }
      };

      // Generate PDF
      await html2pdf().set(opt).from(invoiceRef.current).save();

      // Reset button state
      if (downloadBtn) {
        downloadBtn.disabled = false;
        downloadBtn.innerHTML = originalButtonContent;
      }

      // Remove PDF generating class
      const modalElement = document.querySelector('.fixed.inset-0');
      if (modalElement) {
        modalElement.classList.remove('pdf-generating');
      }

    } catch (error) {
      console.error('PDF generation error:', error);
      alert('Failed to generate PDF. Please try again.');

      // Reset button state on error
      if (downloadBtn) {
        downloadBtn.disabled = false;
        downloadBtn.innerHTML = originalButtonContent;
      }

      // Remove PDF generating class on error
      const modalContainer = document.querySelector('.fixed.inset-0');
      if (modalContainer) {
        modalContainer.classList.remove('pdf-generating');
      }
    }
  };

  // Print Function
  const handlePrint = () => {
    if (!invoiceRef.current) return;

    const printWindow = window.open('', '_blank');
    const content = invoiceRef.current.innerHTML;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
        <head>
          <title>Peaknet Invoice - ${invoice?.invoiceNumber || 'INV'}</title>
          <link href="https://cdn.jsdelivr.net/npm/tailwindcss@2.2.19/dist/tailwind.min.css" rel="stylesheet">
          <style>
            @media print {
              body { margin: 0; }
              .no-print { display: none !important; }
            }
          </style>
        </head>
        <body>
          <div class="p-4">
            ${content}
          </div>
        </body>
      </html>
    `);

    printWindow.document.close();
    printWindow.focus();
    printWindow.print();
    printWindow.close();
  };

  return (
    <>
      <style>
        {`
          @media print {
            .no-pdf { display: none !important; }
            .no-break { page-break-inside: avoid !important; break-inside: avoid !important; page-break-after: avoid !important; }
            .invoice-section-container { page-break-inside: avoid !important; break-inside: avoid !important; page-break-after: avoid !important; }
            table { page-break-inside: auto; }
            thead { display: table-header-group; }
            tfoot { display: table-footer-group; }
            @page {
              margin: 5mm;
            }
          }
          .pdf-generating .no-pdf { display: none !important; }
          /* Additional protection for html2pdf */
          [data-html2pdf-pagebreak="avoid"] {
            page-break-inside: avoid !important;
            break-inside: avoid !important;
            page-break-after: avoid !important;
          }
        `}
      </style>
      <div className="fixed inset-0 bg-black/80 backdrop-blur-xl flex justify-center items-center z-50 p-2 sm:p-4">
        <div className="w-full max-w-5xl max-h-[95vh] overflow-y-auto overflow-x-auto bg-white rounded-2xl shadow-2xl relative">
          {/* Action Buttons - Outside PDF content */}
          <div className="absolute top-2 sm:top-4 right-2 sm:right-4 z-10 hidden sm:flex space-x-2 sm:space-x-3">
            <button
              data-pdf-download
              onClick={generatePDF}
              disabled={isDownloading}
              className={`p-2 sm:p-3 bg-blue-600 hover:bg-blue-700 text-white rounded-lg sm:rounded-xl transition-all duration-300 flex items-center space-x-1 sm:space-x-2 shadow-lg text-sm sm:text-base ${isDownloading ? 'opacity-70 cursor-wait' : ''}`}
            >
              {isDownloading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full mr-2" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FaDownload className="text-sm sm:text-lg" />
                  <span className="hidden sm:inline">Download</span>
                </>
              )}
            </button>
            <button
              onClick={onClose}
              className="p-2 sm:p-3 bg-gray-600 hover:bg-gray-700 text-white rounded-lg sm:rounded-xl transition-all duration-300 shadow-lg"
            >
              <FaTimes className="text-sm sm:text-lg" />
            </button>
          </div>

          {/* Mobile Floating Action Bar */}
          <div className="sm:hidden fixed bottom-0 left-0 right-0 bg-slate-900/95 backdrop-blur-xl border-t border-white/10 p-4 z-[60] flex justify-between items-center space-x-4 safe-area-bottom">
            <button
              onClick={onClose}
              className="flex-1 text-center py-3 text-white font-medium hover:text-[#FFD700] transition-colors"
            >
              Close
            </button>
            <button
              data-pdf-download-mobile
              onClick={generatePDF}
              disabled={isDownloading}
              className={`flex-1 bg-gradient-to-r from-blue-600 to-blue-500 text-white px-4 py-3 rounded-xl font-bold shadow-lg flex items-center justify-center space-x-2 active:scale-95 transition-transform ${isDownloading ? 'opacity-70' : ''}`}
            >
              {isDownloading ? (
                <>
                  <div className="animate-spin h-4 w-4 border-2 border-white border-t-transparent rounded-full" />
                  <span>Generating...</span>
                </>
              ) : (
                <>
                  <FaDownload />
                  <span>Download</span>
                </>
              )}
            </button>
          </div>

          <div ref={invoiceRef} className="min-w-[320px]">
            {/* Header */}
            <div className="sticky top-0 bg-gradient-to-br from-slate-900 via-blue-900 to-indigo-900 text-white p-3 sm:p-6 rounded-t-2xl no-break" data-html2pdf-pagebreak="avoid">
              <div className="flex justify-between items-start">
                <div className="flex items-center space-x-2 sm:space-x-4">
                  <div className="w-20 h-10 sm:w-32 sm:h-14 rounded-lg sm:rounded-xl flex items-center justify-center bg-gradient-to-r from-[#FFD700] to-[#FFB700] shadow-lg">
                    <img src={LOGO_URL} alt="Peaknet" className="h-6 w-auto sm:h-10 select-none" />
                  </div>
                  <div>
                    <h1 className="text-xl sm:text-3xl font-bold">Peaknet</h1>
                    <p className="text-white text-xs sm:text-sm font-medium">PEAK NET PTY LTD</p>
                    <p className="text-white text-xs sm:text-sm font-medium">ABN: 74 689 274 566</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Invoice Details Bar */}
            <div className="bg-green-500 text-white p-3 sm:p-4 no-break" data-html2pdf-pagebreak="avoid">
              <div className="flex flex-wrap justify-between items-center text-center gap-y-4">

                {/* Row 1 on Mobile: Info Fields */}
                <div className="grid grid-cols-3 w-full md:w-3/4 gap-2">
                  <div>
                    <div className="text-xs sm:text-sm opacity-90">Account Number</div>
                    <div className="text-sm sm:text-lg font-bold truncate px-1">{customerData?.accountNumber || customerData?.accountId || invoice?.accountNumber || 'N/A'}</div>
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm opacity-90">Total Amount</div>
                    <div className="text-sm sm:text-lg font-bold">{formatCurrency(invoice?.totalAmount || 0)}</div>
                  </div>
                  <div>
                    <div className="text-xs sm:text-sm opacity-90">Due Date</div>
                    <div className="text-sm sm:text-lg font-bold">{formatDate(invoice?.dueDate || new Date())}</div>
                  </div>
                </div>

                {/* Row 2 on Mobile: Status (Full Width) */}
                <div className="w-full md:w-1/4 flex flex-col justify-center border-t border-white/20 pt-2 md:pt-0 md:border-t-0 md:border-l md:pl-4">
                  <div className="text-xs sm:text-sm opacity-90">Status</div>
                  <div className="flex justify-center mt-1">
                    <span className={`px-6 py-1.5 rounded-full text-base font-bold shadow-sm inline-flex items-center justify-center min-w-[120px] ${getStatusColor(invoice?.status)}`}>
                      {invoice?.status === 'paid' && <span className="mr-2">✓</span>}
                      {invoice?.status?.toUpperCase() || 'PENDING'}
                    </span>
                  </div>
                </div>

              </div>
            </div>

            {/* Main Content */}
            <div className="p-3 sm:p-6 pb-28 sm:pb-6">
              {/* Invoice Header */}
              <div className="mb-4 sm:mb-6 no-break" data-html2pdf-pagebreak="avoid">
                <h2 className="text-xl sm:text-3xl font-bold text-gray-800 mb-2 sm:mb-3">TAX INVOICE</h2>
                <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 sm:gap-6">
                  {/* Customer Information */}
                  <div className="bg-blue-50 p-3 sm:p-4 rounded-xl border border-blue-200 no-break" data-html2pdf-pagebreak="avoid">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                      <FaUser className="mr-2 sm:mr-3 text-blue-600" />
                      Bill To
                    </h3>
                    <div className="space-y-2 sm:space-y-3">
                      <p className="font-semibold text-base sm:text-lg text-gray-800">{customerData?.fullName || 'Customer Name'}</p>
                      <p className="text-gray-600 flex items-start text-sm sm:text-base">
                        <FaMapMarkerAlt className="mr-2 text-blue-600 mt-0.5 flex-shrink-0" />
                        <span className="break-words">{customerData?.address || 'Service Address'}</span>
                      </p>
                      <p className="text-gray-600 flex items-center text-sm sm:text-base">
                        <FaEnvelope className="mr-2 text-blue-600" />
                        {customerData?.email || 'customer@email.com'}
                      </p>
                      <p className="text-gray-600 flex items-center text-sm sm:text-base">
                        <FaPhone className="mr-2 text-blue-600" />
                        {customerData?.phone || 'Phone Number'}
                      </p>
                    </div>
                  </div>

                  {/* Invoice Information */}
                  <div className="bg-green-50 p-3 sm:p-4 rounded-xl border border-green-200 no-break" data-html2pdf-pagebreak="avoid">
                    <h3 className="text-lg sm:text-xl font-semibold text-gray-800 mb-3 sm:mb-4 flex items-center">
                      <FaCalendarAlt className="mr-2 sm:mr-3 text-green-600" />
                      Invoice Details
                    </h3>
                    <div className="space-y-3">
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Invoice Number:</span>
                        <span className="font-bold text-gray-800">{invoice?.invoiceNumber || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Account Number:</span>
                        <span className="font-bold text-gray-800">{customerData?.accountNumber || customerData?.accountId || invoice?.accountNumber || 'N/A'}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Invoice Date:</span>
                        <span className="font-medium text-gray-800">{formatDate(invoice?.invoiceDate || new Date())}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Due Date:</span>
                        <span className="font-medium text-gray-800">{formatDate(invoice?.dueDate || new Date())}</span>
                      </div>
                      <div className="flex justify-between items-center">
                        <span className="text-gray-600 font-medium">Status:</span>
                        <span className={`px-3 py-1 rounded-full text-sm font-semibold ${getStatusColor(invoice?.status)}`}>
                          {invoice?.status?.toUpperCase() || 'PENDING'}
                        </span>
                      </div>
                    </div>
                  </div>
                </div>
              </div>

              {/* Service Details - Simplified for PDF */}
              <div className="mb-6 no-break" data-html2pdf-pagebreak="avoid">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Service Information</h3>
                <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                  <div className="grid grid-cols-2 gap-4">
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Plan</p>
                      <p className="font-semibold text-gray-900">{customerData?.plan || 'N/A'}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Monthly Fee</p>
                      <p className="font-semibold text-gray-900">{formatCurrency(customerData?.monthlyFee || 0)}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Billing Type</p>
                      <p className="font-semibold text-gray-900">{getBillingTypeLabel(invoice?.billingType || 'full-month')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-gray-600 mb-1">Billing Days</p>
                      <p className="font-semibold text-gray-900">{invoice?.billingDays || 30} days</p>
                    </div>
                  </div>
                </div>
              </div>

              {/* Invoice Items - Simplified for PDF */}
              <div className="mb-6 no-break" data-html2pdf-pagebreak="avoid">
                <h3 className="text-lg font-semibold text-gray-800 mb-3">Invoice Items</h3>
                <table className="w-full border border-gray-300" style={{ borderCollapse: 'collapse' }}>
                  <thead className="bg-gray-100">
                    <tr>
                      <th className="text-left p-3 text-sm font-semibold text-gray-700 border-b border-gray-300">Description</th>
                      <th className="text-right p-3 text-sm font-semibold text-gray-700 border-b border-gray-300">Amount</th>
                    </tr>
                  </thead>
                  <tbody>
                    {invoice?.items && invoice.items.length > 0 ? (
                      invoice.items.map((item, index) => (
                        <tr key={index} className="border-b border-gray-200">
                          <td className="p-3 text-gray-900">
                            <p className="font-medium">{item.description}</p>
                            {invoice.billingType === 'pro-rated' && invoice.billingDays && (
                              <p className="text-xs text-gray-500">Pro-rated for {invoice.billingDays} days</p>
                            )}
                          </td>
                          <td className="p-3 text-right text-gray-900 font-semibold">{formatCurrency(item.amount)}</td>
                        </tr>
                      ))
                    ) : (
                      <tr className="border-b border-gray-200">
                        <td className="p-3 text-gray-900">
                          <p className="font-medium">{customerData?.plan || 'Monthly Service'} - {getBillingTypeLabel(invoice?.billingType)}</p>
                          {invoice.billingType === 'pro-rated' && invoice.billingDays && (
                            <p className="text-xs text-gray-500">Pro-rated for {invoice.billingDays} days</p>
                          )}
                        </td>
                        <td className="p-3 text-right text-gray-900 font-semibold">{formatCurrency(invoice?.totalAmount || 0)}</td>
                      </tr>
                    )}
                  </tbody>
                  <tfoot className="bg-gray-50">
                    <tr>
                      <td className="p-3 text-right font-semibold text-gray-700 border-t border-gray-300">Subtotal:</td>
                      <td className="p-3 text-right font-semibold text-gray-900 border-t border-gray-300">{formatCurrency(invoice?.subtotal || invoice?.totalAmount || 0)}</td>
                    </tr>
                    {invoice?.surcharge > 0 ? (
                      <tr>
                        <td className="p-3 text-right font-semibold text-gray-700">Payment Processing Surcharge:</td>
                        <td className="p-3 text-right font-semibold text-orange-600">{formatCurrency(invoice.surcharge)}</td>
                      </tr>
                    ) : null}
                    <tr>
                      <td className="p-3 text-right font-semibold text-gray-700">Tax (GST):</td>
                      <td className="p-3 text-right font-semibold text-gray-900">{formatCurrency(invoice?.tax || 0)}</td>
                    </tr>
                    <tr className="bg-white">
                      <td className="p-3 text-right font-bold text-lg text-gray-800 border-t-2 border-gray-400">Total:</td>
                      <td className="p-3 text-right font-bold text-lg text-blue-600 border-t-2 border-gray-400">{formatCurrency(invoice?.totalAmount || 0)}</td>
                    </tr>
                  </tfoot>
                </table>
              </div>

              {/* Payment Information - Simplified for PDF */}
              {invoice.status !== 'paid' && (
                <div className="mb-6 invoice-section-container no-break" data-html2pdf-pagebreak="avoid" style={{ pageBreakInside: 'avoid', pageBreakAfter: 'avoid', breakInside: 'avoid' }}>
                  <h3 className="text-lg font-semibold text-gray-800 mb-3">Payment Options</h3>
                  <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Direct Debit:</strong> Set up automatic payments from your nominated account
                    </p>
                    <p className="text-sm text-gray-700 mb-2">
                      <strong>Online Payment:</strong> Pay securely through our customer portal
                    </p>
                    <p className="text-sm text-gray-700">
                      <strong>Phone Payment:</strong> Call 1300 606 766 to pay over the phone
                    </p>
                  </div>
                </div>
              )}

              {/* About Your Bill - New Section */}
              <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4 no-break" data-html2pdf-pagebreak="avoid">
                <h3 className="text-base font-semibold text-gray-800 mb-3 border-b border-gray-200 pb-2">About Your Bill</h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-xs text-gray-700">

                  {/* Payment Assistance */}
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">Need extra help?</h4>
                    <p className="mb-2">
                      If you are feeling overwhelmed by your financial situation, we can support you with payment assistance.
                      Contact us on <strong>1300 606 766</strong> or via email at <a href="mailto:support@peaknet.com.au" className="text-blue-600 hover:underline">support@peaknet.com.au</a>.
                    </p>
                    <p>
                      To learn more about payment assistance options visit <a href="https://peaknet.com.au/financial-hardship" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">peaknet.com.au/financial-hardship</a>.
                      We're here for you.
                    </p>
                  </div>

                  {/* Bill Explainer */}
                  <div>
                    <h4 className="font-bold text-gray-800 mb-1">Need help understanding your bill?</h4>
                    <p className="mb-3">
                      Check out our Bill Explainer video at <a href="https://peaknet.com.au/help/billing" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">peaknet.com.au/help/billing</a> and take a detailed look at your Peaknet bill.
                    </p>
                  </div>

                  {/* Direct Debit Info */}
                  <div className="md:col-span-2 mt-2">
                    <h4 className="font-bold text-gray-800 mb-1">Direct Debit</h4>
                    <p className="mb-2">
                      Pay your Peaknet account the easy way with direct debit. Direct debit payments will be deducted from your nominated bank account or credit/debit card on the due date of your bill.
                      If a direct debit payment is unsuccessful you will be notified via email. We will automatically reattempt unsuccessful payments 7 days after the due date.
                    </p>
                    <p>
                      If you need to update your payment details, login to your ‘Billing’ at <a href="https://members.peaknet.com.au/login" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">https://members.peaknet.com.au/login</a>
                    </p>
                  </div>

                  {/* Privacy Policy */}
                  <div className="md:col-span-2 border-t border-gray-200 pt-2 mt-2">
                    <h4 className="font-bold text-gray-800 mb-1">Updates to our Privacy Policy</h4>
                    <p>
                      We regularly update our Privacy Policy to clarify how we manage your personal information.
                      View our Privacy Policy at <a href="https://peaknet.com.au/privacy" target="_blank" rel="noreferrer" className="text-blue-600 hover:underline">peaknet.com.au/privacy</a>
                    </p>
                  </div>
                </div>
              </div>

              {/* Payment Method Info - Simplified for PDF */}
              {invoice.paymentMethod && (
                <div className="mb-6 bg-gray-50 border border-gray-200 rounded-lg p-4 invoice-section-container no-break" data-html2pdf-pagebreak="avoid" style={{ pageBreakInside: 'avoid', pageBreakAfter: 'avoid', breakInside: 'avoid' }}>
                  <h3 className="text-base font-semibold text-gray-800 mb-3">Payment Information</h3>
                  <div className="space-y-2 text-sm text-gray-700">
                    <p><strong>Payment Method:</strong> {invoice.paymentMethod === 'card' || invoice.paymentMethod === 'stripe' ? 'Credit Card' : 'Direct Debit'}</p>
                    {invoice.paymentDate && (
                      <p><strong>Paid Date:</strong> {formatDate(invoice.paymentDate)}</p>
                    )}
                    {invoice?.surcharge > 0 && (
                      <p className="text-orange-600"><strong>Surcharge:</strong> {invoice.paymentMethod === 'card' || invoice.paymentMethod === 'stripe' ? 'Credit Card Surcharge Applied' : 'Customer pays surcharge'}</p>
                    )}
                  </div>
                </div>
              )}

              {/* Contact Information - Simplified for PDF */}
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200 invoice-section-container no-break" data-html2pdf-pagebreak="avoid" style={{ pageBreakInside: 'avoid', pageBreakAfter: 'avoid', breakInside: 'avoid' }}>
                <h3 className="text-base font-semibold text-gray-800 mb-3">Contact Information</h3>
                <div className="space-y-1 text-sm text-gray-700">
                  <p><strong>Billing:</strong> billing@peaknet.com.au (Mon-Fri 8am - 6pm AEST)</p>
                  <p><strong>Support:</strong> support@peaknet.com.au</p>
                  <p><strong>Portal:</strong> members.peaknet.com.au</p>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="bg-gray-50 p-6 rounded-b-2xl border-t no-break" data-html2pdf-pagebreak="avoid">
              <div className="text-center">
                <p className="text-gray-600 font-medium">Thank you for choosing Peaknet!</p>
                <p className="text-sm text-gray-500 mt-2">
                  For any questions, please contact us at{' '}
                  <a href="mailto:billing@peaknet.com.au" className="text-blue-600 hover:underline font-medium">
                    billing@peaknet.com.au
                  </a>
                </p>
              </div>
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default ProfessionalInvoiceTemplate;
